import React, { useEffect, useRef, useState } from "react";
import RoomsLeft from "../../assets/images/rooms-left.svg";
import AdultIcon from "../../assets/images/adults-icon.svg";
import { formatMoney, getTenantDomain } from "../../helper";
import { useSelector, useDispatch } from "react-redux";
import {
  addRoomItem,
  removeRoomItem,
  updateRoomPrice,
} from "../../_actions/stateActions";
import PropertyAmenities from "./PropertyAmenities";
import { handleImageError } from "../../helper";
import { useTranslation } from "react-i18next";
// import AvailabilityItem from "./AvailabilityItem";
import { FaPlus, FaMinus, FaChevronDown } from "react-icons/fa";
import {
  getOccupancyRoomPrice,
  getVisibleOccupancies,
} from "../../helper/occupancyPrices";
import {
  cartSelections,
  changeSlot,
  fallbackOccupancy,
  getSelectedApartments,
  getSelectedGuests,
  guestCoverage,
  limitToCapacity,
  occupancyOptionLabel,
  visibleSlots,
} from "../../helper/roomSelection";
import { translateCount } from "../../helper/plural";
import { totalGuests as countGuests } from "../../helper/guests";
import { nightsBetween } from "../../helper/stayRange";
import { feeOf } from "../../helper/bookingPrice";

const toDate = (value: any) => (value ? new Date(value) : null);

// Nights of the searched stay.
const useStayNights = (): number => {
  const { arrivalDate, departureDate } = useSelector((state: any) => state.search);

  return nightsBetween(toDate(arrivalDate), toDate(departureDate));
};

// Texts with a number in the plural form of the current language.
const useCountText = () => {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage || i18n.language || "en";

  return (key: string, count: number): string =>
    translateCount(t, language, key, count);
};

// What is paid on top of the apartment: its cleaning fee (or the property
// fee when it has none) and the city tax per adult and night, if charged.
const FeeNotes: React.FC<{ cleaningFee: number; cityTaxRate: number; currency: any }> = ({
  cleaningFee,
  cityTaxRate,
  currency,
}) => {
  const { t } = useTranslation();
  const notes = [
    cleaningFee > 0 &&
      t("plus_cleaning_fee", {
        amount: formatMoney(cleaningFee, 2, currency),
        defaultValue: "+ cleaning {{amount}}",
      }),
    cityTaxRate > 0 &&
      t("plus_city_tax", {
        amount: formatMoney(cityTaxRate, 2, currency),
        defaultValue: "+ city tax {{amount}} per adult and night",
      }),
  ].filter(Boolean);

  return (
    <>
      {notes.map((note) => (
        <p key={String(note)} className="fee-note">
          {note}
        </p>
      ))}
    </>
  );
};

// Aparthotel: whether the picked apartments fit the searched guests and what to do next.
const SelectionSummary: React.FC<{ rooms: any[]; guests: number }> = ({
  rooms,
  guests,
}) => {
  const { t } = useTranslation();
  const countText = useCountText();
  const nights = useStayNights();
  const selected = getSelectedGuests(rooms);
  const coverage = guestCoverage(selected, guests);

  if (coverage === "none") {
    return null;
  }

  const text =
    coverage === "missing"
      ? t("selection_missing", {
          guests: countText("for_guests", guests - selected),
          defaultValue: "You still need space {{guests}}.",
        })
      : coverage === "exact"
      ? t("selection_exact", {
          apartments: countText("apartments_count", getSelectedApartments(rooms)),
          guests: countText("for_guests", selected),
          nights: countText("nights_count", nights),
          defaultValue: "Selected: {{apartments}} {{guests}}, {{nights}}",
        })
      : t("selection_over", {
          selected: countText("for_guests", selected),
          guests: countText("for_guests", guests),
          defaultValue:
            "You selected space {{selected}}, but you are searching {{guests}}. Change the number of guests or remove an apartment.",
        });

  return (
    <p
      className={`aparthotel-selection aparthotel-selection--${coverage}`}
      role="status"
    >
      {coverage === "exact" && <span aria-hidden="true">✓ </span>}
      {text}
    </p>
  );
};

interface HotelAvailabilityBoxProps {
  property: any;
  tenant: string;
  images: any;
}

const HotelAvailabilityBox: React.FC<HotelAvailabilityBoxProps> = ({
  property,
  tenant,
  images,
}) => {
  const { t } = useTranslation();
  const persons = useSelector((state: any) => state.search.persons);
  const totalGuests = countGuests(persons);
  const cartRooms = useSelector((state: any) => state.cart.rooms);
  const isApartHotel = property?.property_type === "apart_hotel";
  return (
    <div className="availability-wrapper">
      <h3>{t("availability")}</h3>
      {/* Once apartments are picked, the selection summary takes over */}
      {property?.requires_multiple_rooms &&
        !(isApartHotel && getSelectedGuests(cartRooms) > 0) && (
        <p className="select-room-hint">
          {t("requires_multiple_rooms", { guests: totalGuests })}
        </p>
      )}
      {isApartHotel && (
        <SelectionSummary rooms={cartRooms} guests={totalGuests} />
      )}
      {property?.room_types.map((room_type: any) => {
        return (
          <RoomTypeBox
            key={room_type?.id}
            room_type={room_type}
            tenant={tenant}
            propertyImages={images}
            selectedGuests={totalGuests}
            isApartHotel={isApartHotel}
            propertyCleaningFee={feeOf(property?.iframe_settings)}
            cityTaxRate={Number(property?.city_tax_rate) || 0}
          />
        );
      })}
    </div>
  );
};

export default HotelAvailabilityBox;

// Room Type Box
interface RoomTypeBoxProps {
  room_type: any;
  tenant: string;
  propertyImages: any;
  selectedGuests: number;
  isApartHotel: boolean;
  propertyCleaningFee?: number;
  cityTaxRate?: number;
}
const RoomTypeBox: React.FC<RoomTypeBoxProps> = ({
  room_type,
  tenant,
  propertyImages,
  selectedGuests,
  isApartHotel,
  propertyCleaningFee = 0,
  cityTaxRate = 0,
}) => {
  const { i18n, t } = useTranslation();
  const countText = useCountText();
  const nights = useStayNights();
  const [pickedGuests, setPickedGuests] = useState<number[]>([]);
  const width = "160px";
  const imagePath = `${getTenantDomain(tenant)}/tenancy/assets/`;
  const [totalPrice, setTotalPrice] = useState<number>(0);
  const currency = useSelector((state: any) => state.core.currency);
  const [selectedRoomCount, setSelectedRoomCount] = useState<number>(0);
  const remaingRooms =
    (room_type?.availability?.[0]?.availability ?? 1) -
      (room_type?.availability?.[0]?.booked_count ?? 0) || 1;
  const imageUrl = (images: any) => {
    return images?.length > 0
      ? `${imagePath}properties/gallery/thumbnails/${images[0]?.filename}`
      : propertyImages
      ? `${imagePath}properties/gallery/thumbnails/${propertyImages[0]?.filename}`
      : "https://via.placeholder.com/" + width;
  };

  const room_benefits = room_type?.iframe_settings?.benefits_text;
  const currentLanguage = i18n.resolvedLanguage || "en";
  const benefitsContent =
    room_benefits?.[currentLanguage] || room_benefits?.["en"] || "";

  // Convert benefits content to features array
  const getFeaturesFromBenefits = (content: string) => {
    if (!content) {
      return "";
    }
    return content;
  };

  const features = getFeaturesFromBenefits(benefitsContent);
  // Rows above the room capacity cannot be booked
  const occupancies = limitToCapacity(
    getVisibleOccupancies(room_type?.iframe_settings?.prices),
    room_type?.default_occupancy,
  );
  const offeredOccupancies =
    occupancies.length > 0
      ? occupancies
      : [fallbackOccupancy(selectedGuests, room_type?.default_occupancy)];
  // The apartment's own fee, otherwise the fee of the property.
  const cleaningFee = feeOf(room_type?.iframe_settings) || propertyCleaningFee;
  const prices = offeredOccupancies
    .map((guests) => getOccupancyRoomPrice(room_type, guests))
    .filter((price) => price > 0);
  const fromPrice = prices.length > 0 ? Math.min(...prices) : 0;
  const feeNotes = (
    <FeeNotes cleaningFee={cleaningFee} cityTaxRate={cityTaxRate} currency={currency} />
  );

  return (
    <div className="availability-box">
      <div className="room-intro">
        <div className="image-holder">
          <img
            src={imageUrl(room_type?.galleries)}
            alt="Room"
            onError={handleImageError}
          />
        </div>
        <div className="info">
          <h4>{room_type?.title}</h4>
          {/* Every aparthotel apartment is unique, so "rooms left" says nothing there */}
          {!isApartHotel && (
            <p>
              <img src={RoomsLeft} alt="Rooms left" />
              <span style={{ color: "#CE2323" }}>
                {t("rooms_left", { count: remaingRooms })}
              </span>
            </p>
          )}
        </div>
      </div>
      {/* amenties box */}
      <PropertyAmenities
        amenities={room_type?.amenities}
        title={t("amenities")}
        limit={6}
        compact
      />
      {/* amenties box */}

      {/* Availability Container */}
      <div className="availability-container">
        {isApartHotel ? (
          <OccupancyPicker
            occupancies={offeredOccupancies}
            room_type={room_type}
            currency={currency}
            features={features}
            remaingRooms={remaingRooms}
            setTotalPrice={setTotalPrice}
            setSelectedRoomCount={setSelectedRoomCount}
            onSelectionsChange={setPickedGuests}
          />
        ) : (
          <>
            <div className="availability-header">
              <span>{t("guests")}</span>
              <span>{t("price")}</span>
              <span>{t("choice")}</span>
            </div>

            {/* One row per offered occupancy; without per-occupancy prices show the searched guests */}
            {offeredOccupancies.map((guests) => (
              <AvailabilityItem
                key={guests}
                guests={guests}
                roomTypePrice={getOccupancyRoomPrice(room_type, guests)}
                currency={currency}
                setTotalPrice={setTotalPrice}
                remaingRooms={remaingRooms}
                selectedRoomCount={selectedRoomCount}
                setSelectedRoomCount={setSelectedRoomCount}
                room_type_id={room_type?.id}
                title={room_type?.title}
                features={features}
              />
            ))}
          </>
        )}

        {/* Summary Section */}
        {isApartHotel ? (
          <div className="summary">
            <div className="summary-text">
              {pickedGuests.length === 0 ? (
                <strong>{t("nothing_selected", "Nothing selected yet")}</strong>
              ) : (
                <>
                  <strong>
                    {t("selected_label", {
                      value:
                        pickedGuests.length > 1
                          ? `${countText("apartments_count", pickedGuests.length)} · ${countText(
                              "guests_count",
                              pickedGuests.reduce((sum, guests) => sum + guests, 0),
                            )}`
                          : countText("guests_count", pickedGuests[0]),
                      defaultValue: "Selected: {{value}}",
                    })}
                  </strong>
                  <p>{t("confirmation_is_immediate")}</p>
                </>
              )}
            </div>
            {/* Before a choice, the cheapest row shows what the apartment costs */}
            {(pickedGuests.length > 0 || fromPrice > 0) && (
              <div className="summary-price">
                <strong>
                  {pickedGuests.length > 0
                    ? formatMoney(totalPrice, 2, currency)
                    : `${t("from")} ${formatMoney(fromPrice, 2, currency)}`}
                </strong>
                {nights > 0 && (
                  <p>
                    {t("price_for_nights", {
                      nights: countText("nights_count", nights),
                      defaultValue: "for {{nights}}",
                    })}
                  </p>
                )}
                {feeNotes}
              </div>
            )}
          </div>
        ) : (
          <div className="summary">
            <div className="summary-text">
              <strong>{t("rooms_for", { room_count: selectedRoomCount })}</strong>
              <p>{t("confirmation_is_immediate")}</p>
            </div>
            <div className="summary-price">
              <strong>{formatMoney(totalPrice, 2, currency)}</strong>
              {feeNotes}
            </div>
          </div>
        )}

        {/* <div className='reserve-button'>
          <button>{t("choose_this_option")}</button>
        </div> */}
      </div>
    </div>
  );
};

// Aparthotel: one dropdown per apartment with the guests and price of each occupancy
interface OccupancyPickerProps {
  occupancies: number[];
  room_type: any;
  currency: any;
  features: string;
  remaingRooms: number;
  setTotalPrice: React.Dispatch<React.SetStateAction<number>>;
  setSelectedRoomCount: React.Dispatch<React.SetStateAction<number>>;
  onSelectionsChange: (selections: number[]) => void;
}

const OccupancyPicker: React.FC<OccupancyPickerProps> = ({
  occupancies,
  room_type,
  currency,
  features,
  remaingRooms,
  setTotalPrice,
  setSelectedRoomCount,
  onSelectionsChange,
}) => {
  const { t } = useTranslation();
  const countText = useCountText();
  const dispatch = useDispatch();
  const cartRooms = useSelector((state: any) => state.cart.rooms);
  const [selections, setSelections] = useState<number[]>(() =>
    cartSelections(cartRooms, room_type?.id),
  );
  const priceOf = (guests: number) => getOccupancyRoomPrice(room_type, guests);

  useEffect(() => {
    setSelectedRoomCount(selections.length);
    onSelectionsChange(selections);
    setTotalPrice(
      selections.reduce((total, guests) => total + priceOf(guests), 0),
    );
    // priceOf only reads room_type
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selections, room_type, setSelectedRoomCount, setTotalPrice, onSelectionsChange]);

  const handleChange = (slot: number, value: string) => {
    const guests = value === "" ? null : Number(value);
    const { next, removed, added } = changeSlot(selections, slot, guests);
    const cartRoom = { room_type_id: room_type?.id, name: room_type?.title };
    if (removed !== null) {
      dispatch(
        removeRoomItem({ ...cartRoom, index: removed, price: priceOf(removed) }),
      );
    }
    if (added !== null) {
      dispatch(
        addRoomItem({
          ...cartRoom,
          index: added,
          count: 1,
          price: priceOf(added),
        }),
      );
    }
    setSelections(next);
  };

  return (
    <div className="occupancy-picker">
      <span className="occupancy-picker-label">{t("number_of_guests")}</span>
      {visibleSlots(selections, remaingRooms).map((selected, slot) => (
        <div className="occupancy-select" key={slot}>
          <select
            aria-label={t("number_of_guests")}
            value={selected ?? ""}
            onChange={(event) => handleChange(slot, event.target.value)}
          >
            <option value="">{t("occupancy_not_selected")}</option>
            {occupancies.map((guests) => (
              <option key={guests} value={guests}>
                {occupancyOptionLabel(
                  countText("guests_count", guests),
                  formatMoney(priceOf(guests), 2, currency),
                )}
              </option>
            ))}
          </select>
          <FaChevronDown aria-hidden="true" />
        </div>
      ))}
      {features && (
        <div
          className="occupancy-picker-benefits"
          dangerouslySetInnerHTML={{ __html: features }}
        />
      )}
    </div>
  );
};

// Define props type
interface AvailabilityItemProps {
  guests: number;
  features: string;
  roomTypePrice: number;
  currency: any;
  setTotalPrice: React.Dispatch<React.SetStateAction<number>>;
  remaingRooms: number;
  selectedRoomCount: number;
  setSelectedRoomCount: React.Dispatch<React.SetStateAction<number>>;
  room_type_id: number | string;
  title: string;
}

const AvailabilityItem: React.FC<AvailabilityItemProps> = ({
  guests,
  features,
  roomTypePrice,
  currency,
  setTotalPrice,
  remaingRooms,
  selectedRoomCount,
  setSelectedRoomCount,
  room_type_id,
  title,
}) => {
  const { t } = useTranslation();
  const [count, setCount] = useState<number>(0);
  const prevCountRef = useRef<number>(0);
  const prevPriceRef = useRef<number>(0);
  const dispatch = useDispatch();
  // Price for this row's occupancy, already adjusted by the API; it is what the cart charges
  const originalPrice = roomTypePrice;
  const handleIncrease = () => {
    if (selectedRoomCount >= remaingRooms) {
      alert(t("no_more_rooms_available"));
      return;
    }
    setSelectedRoomCount((prev) => prev + 1);
    setCount(count + 1);
    const roomObj = {
      room_type_id,
      index: guests,
      count: count + 1,
      price: originalPrice,
      name: title,
    };
    dispatch(addRoomItem(roomObj));
  };
  const handleDecrease = () => {
    if (count === 0) return;
    setSelectedRoomCount((prev) => prev - 1);
    setCount(count - 1);
    const roomObj = {
      room_type_id,
      index: guests,
      count: count - 1,
      price: originalPrice,
      name: title,
    };
    dispatch(removeRoomItem(roomObj));
  };

  useEffect(() => {
    const newTotal = count * originalPrice;
    const oldTotal = prevCountRef.current * prevPriceRef.current;
    const priceChange = newTotal - oldTotal;
    setTotalPrice((prev) => prev + priceChange);

    if (count > 0 && originalPrice !== prevPriceRef.current) {
      dispatch(
        updateRoomPrice({
          room_type_id,
          index: guests,
          price: originalPrice,
        }),
      );
    }

    prevCountRef.current = count;
    prevPriceRef.current = originalPrice;
  }, [count, originalPrice, setTotalPrice, dispatch, room_type_id, guests]);

  return (
    <div className="availability-item">
      <div className="guests">
        <span
          style={{
            marginRight: "4px",
            minWidth: "1ch",
            textAlign: "right",
            display: "inline-block",
          }}
        >
          {guests}
        </span>
        <img src={AdultIcon} alt="Adult Icon" />
      </div>

      <div className="price">
        <strong>{formatMoney(originalPrice, 2, currency)}</strong>
      </div>

      <div className="choice">
        <div dangerouslySetInnerHTML={{ __html: features }} />
        <div className="counter">
          <button
            className="minus"
            onClick={handleDecrease}
            disabled={count === 0}
          >
            <FaMinus />
          </button>
          <span>{count}</span>
          <button className="plus" onClick={handleIncrease}>
            <FaPlus />
          </button>
        </div>
      </div>
    </div>
  );
};

